import { formatDateTime, type ActivityDto } from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { useFilter } from '../app/FilterProvider.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { api, query } from '../lib/api.js';
import { cn } from '../lib/cn.js';

type ActorFilter = 'ALLE' | 'USER' | 'AGENT' | 'SYSTEM';

const ACTOR_LABELS: Record<ActorFilter, string> = {
  ALLE: 'Alle',
  USER: 'Mennesker',
  AGENT: 'Nova',
  SYSTEM: 'System',
};

export function ActivityPage() {
  const { areaId } = useFilter();
  const [actorType, setActorType] = useState<ActorFilter>('ALLE');
  const [search, setSearch] = useState('');
  const [take, setTake] = useState(50);

  const { data, isLoading } = useQuery({
    queryKey: ['activity', { actorType, search, take, areaId }],
    queryFn: () =>
      api.get<{ items: ActivityDto[]; total: number }>(
        `/activity${query({
          actorType: actorType === 'ALLE' ? undefined : actorType,
          search: search.trim() || undefined,
          areaId: areaId ?? undefined,
          take,
        })}`,
      ),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-display text-ink text-xl">Aktivitetslogg</h1>
        <p className="text-ink-soft mt-0.5 text-sm">
          Alt som skjer i Nova — agenthandlinger, statusendringer, godkjenninger og varsler.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="border-hairline bg-panel/60 flex rounded-lg border p-0.5">
          {(Object.keys(ACTOR_LABELS) as ActorFilter[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActorType(key)}
              className={cn(
                'rounded-md px-2.5 py-1.5 text-xs transition',
                actorType === key ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
              )}
            >
              {ACTOR_LABELS[key]}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search
            size={14}
            className="text-ink-soft pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            aria-hidden
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk i loggen"
            aria-label="Søk i aktivitetsloggen"
            className="border-hairline bg-panel/60 text-ink placeholder:text-ink-soft/60 h-9 w-64 rounded-lg border pl-8 pr-3 text-xs"
          />
        </div>

        {data && (
          <span className="text-ink-soft text-xs">
            Viser {items.length} av {data.total}
          </span>
        )}
      </div>

      <GlassPanel>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonPanel key={i} lines={1} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Ingen oppføringer matcher søket." />
        ) : (
          <ol className="border-hairline space-y-3 border-l pl-5">
            {items.map((entry) => (
              <li key={entry.id} className="relative">
                <span
                  aria-hidden
                  className={cn(
                    'absolute -left-[25px] top-1.5 h-2 w-2 rounded-full',
                    entry.actorType === 'AGENT'
                      ? 'bg-violet'
                      : entry.actorType === 'SYSTEM'
                        ? 'bg-ink-soft'
                        : 'bg-accent',
                  )}
                />
                <p className="text-ink-soft text-sm leading-snug">{entry.message}</p>
                <p className="text-ink-soft/70 mt-0.5 text-[11px]">
                  {formatDateTime(entry.createdAt)} · {entry.actorName} · {entry.action}
                </p>
              </li>
            ))}
          </ol>
        )}

        {data && items.length < data.total && (
          <div className="mt-4 flex justify-center">
            <Button size="sm" onClick={() => setTake((t) => t + 50)}>
              Vis flere
            </Button>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}
